import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { UserPermission } from '@/types/user';
import { CAN_ACCESS_ANALYTICS_DASHBOARD } from '@/utils/constants';
import { AuthService } from '../../../services/auth-service';

export const useGetUserPermissions = () => {
  return useQuery({
    queryKey: ['userPermissions'],
    queryFn: () => AuthService.getUserPermissions(),
  });
};

/** Whether the signed-in user's permissions include `codename`; false while loading. */
export const useHasPermission = (codename: string) => {
  const { data, isPending } = useGetUserPermissions();
  const hasPermission = !!(data?.results as UserPermission[] | undefined)?.some(
    (p) => p.codename === codename
  );
  return { hasPermission, isPending };
};

export const useCanAccessAnalyticsDashboard = () =>
  useHasPermission(CAN_ACCESS_ANALYTICS_DASHBOARD);

export const useGetUserPermissionsById = (userId: string | number) => {
  return useQuery({
    queryKey: ['userMyPermissions', userId],
    queryFn: () => AuthService.getUserPermissionsById(userId),
  });
};

interface UseUpdateUserPermissionsOptions {
  onSuccess?: () => void;
  successMessage?: string;
}

export const useUpdateUserPermissions = ({
  onSuccess,
  successMessage = 'Permissions updated successfully',
}: UseUpdateUserPermissionsOptions = {}) => {
  return useMutation({
    mutationFn: async ({
      userId,
      permissions,
    }: {
      userId: string | number;
      permissions: any;
    }) => {
      const response = await AuthService.updateUserPermissions(
        userId,
        permissions
      );
      return response;
    },
    onSuccess: (data) => {
      toast.success(successMessage, { position: 'top-right' });
      onSuccess?.();
    },
    onError: (error) => {
      toast.error('Failed to update permissions. Please try again.');
      console.error('Failed to update permissions:', error);
    },
  });
};
